import { useEffect } from 'react';
import List from '@mui/material/List';
import type { GroupedOrderProduct, Nullish } from '@witmetrics/utils';
import {
  useActivePractice,
  useDispatch,
  useLab,
  useProject,
} from '@witmetrics/data-store';
import { arrayMove, findIndexByKey } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { showSuccessToaster } from '@/utils/toasters';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import { useId } from '@/hooks/useId';
import { useString } from '@/hooks/useString';
import { useArray } from '@/hooks/useArray';
import { useInvalidFields } from '@/hooks/useInvalidFields';
import type { DraftOrderProduct } from '@/types/orders';
import Divider from '@/components/Divider';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import ProjectField from './ProjectField';
import NotesField from './NotesField';
import ExternalRefField from './ExternalRefField';
import NewProductField from './NewProductField';
import OrderListItem from './OrderListItem';
import LabField from './LabField';
import {
  buildNewProduct,
  buildOrderData,
  classes,
  createNewOrder,
  fetchLabProducts,
  fetchRecentProjects,
  sumTotal,
  validateFields,
} from './utils';

export type PracticeOrderDialogProps = {
  projectId?: number | Nullish;
  onClose: () => void;
};

export default function PracticeOrderDialog({
  projectId,
  onClose,
}: PracticeOrderDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const activePractice = useActivePractice();
  const [activeProjectId, setProjectId] = useId(projectId ?? undefined);
  const [activeLabId, setLabId] = useId();
  const project = useProject(activeProjectId);
  const lab = useLab(activeLabId);
  const [externalRef, setExternalRef] = useString();
  const [notes, setNotes] = useString();
  const [
    orderProducts,
    setProducts,
    {
      clear: clearProducts,
      appendItem: appendProduct,
      replaceItem: replaceProduct,
      removeItem: removeProduct,
    },
  ] = useArray<GroupedOrderProduct>();
  const { setInvalidFields, isValidField } = useInvalidFields();
  const [isSaving, toggleSaving] = useToggle();

  useEffect(() => {
    if (activeLabId) fetchProducts();
  }, [activeLabId]);

  const fetchProducts = async () => {
    try {
      dispatch.addProducts(
        await fetchLabProducts(activePractice!.id, activeLabId!)
      );
      if (!projectId) {
        // If there's no projectId, fetch the 10 most recently updated projects
        dispatch.addProjects(await fetchRecentProjects());
      }
    } catch (err) {
      onApiError(err, 'Error fetching products', () => fetchProducts());
    }
  };

  const handleSelectLab = (labId: number | undefined) => {
    if (labId !== activeLabId) {
      clearProducts(); // Can only add products from selected lab
      setLabId(labId);
    }
  };

  const handleAddProduct = (newProduct: DraftOrderProduct) => {
    const existingIndex = findIndexByKey(
      orderProducts,
      'productId',
      newProduct.productId
    );
    if (existingIndex > -1) {
      // Product already in list, increment quantity and bring to top
      let updatedProducts = [...orderProducts];
      updatedProducts[existingIndex].quantity++;
      setProducts(arrayMove(updatedProducts, existingIndex, 0));
    } else {
      appendProduct(buildNewProduct(newProduct));
    }
  };

  const handleSave = async () => {
    try {
      toggleSaving(true);
      const invalid = validateFields(activeLabId, orderProducts);
      if (!isEmpty(invalid)) return setInvalidFields(invalid);
      const result = await createNewOrder(
        buildOrderData(
          activeLabId!,
          activePractice!.id,
          externalRef,
          orderProducts
        ),
        activeProjectId,
        notes
      );
      dispatch.addOrders([result.order]);
      if (result.unisonProjectOrder) {
        dispatch.addProjectOrders([result.unisonProjectOrder]);
      }
      if (result.orderNote) dispatch.addOrderNotes([result.orderNote]);
      showSuccessToaster('Order created');
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving order', () => handleSave());
    }
  };

  const isSaveDisabled = () => {
    return isSaving || !isEmpty(validateFields(activeLabId, orderProducts));
  };

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle title="New order" onClose={onClose} />
      <DialogBody className={classes.body}>
        <div className={classes.wrapper}>
          <form className={classes.form}>
            <LabField
              className={classes.input}
              value={lab ? { label: lab.name, value: lab.id } : null}
              onSelect={(update) => handleSelectLab(update?.value)}
            />
            <ProjectField
              className={classes.input}
              value={
                project ? { label: project.name, value: project.id } : null
              }
              onSelect={(update) => setProjectId(update?.value)}
            />
            <ExternalRefField
              error={!isValidField('externalRef')}
              className={classes.input}
              value={externalRef}
              onChange={setExternalRef}
            />
            <NotesField
              className={classes.input}
              value={notes}
              onChange={setNotes}
            />
          </form>
          <Divider
            className={classes.divider}
            orientation="vertical"
            variant="middle"
          />
          <div className={classes.formWrapper}>
            {!activeLabId && (
              <div className={classes.message}>
                To add products, please first select a lab.
              </div>
            )}
            {activeLabId && (
              <NewProductField
                labId={activeLabId}
                onSelect={handleAddProduct}
              />
            )}
            <List className={classes.list}>
              {orderProducts.map((p, i) => (
                <OrderListItem
                  key={p.uuid}
                  labId={activeLabId!}
                  groupedProduct={p}
                  onChange={(update) => replaceProduct(i, update)}
                  onRemove={() => removeProduct(i)}
                />
              ))}
            </List>
            {!isEmpty(orderProducts) && (
              <>
                <Divider />
                <div className={classes.totalWrapper}>
                  Total:
                  <span className={classes.total}>
                    {sumTotal(orderProducts)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogBody>
      <DefaultDialogActions
        disabled={isSaveDisabled()}
        pending={isSaving}
        confirmLabel="Save"
        pendingLabel="Saving"
        onCancel={onClose}
        onConfirm={handleSave}
      />
    </Dialog>
  );
}
